import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { HabitFormData } from "@/components/habit/AddHabitDialog";

// Haversine formula to calculate distance between two coordinates in meters
const getDistanceFromLatLng = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Storage key for tracking celebrated frogs
const CELEBRATED_FROGS_KEY = "habit_frog_celebrated_adults";

interface Habit {
  id: string;
  name: string;
  frequencyType: string;
  frequencyCount: number;
  evidenceType: string | null;
  locationLat: number | null;
  locationLng: number | null;
  locationRadius: number | null;
  scheduledTime: string | null;
  timezone: string | null;
  frog: {
    id: string;
    stage: "egg" | "tadpole" | "young" | "adult" | "hibernating";
    daysCompleted: number;
    currentStreak: number;
    positionX: number;
    positionY: number;
  };
}

const getRandomPosition = (existingPositions: { x: number; y: number }[]) => {
  let x: number, y: number;
  let attempts = 0;
  const minDistance = 20;

  do {
    x = 15 + Math.random() * 55; // Keep within pond bounds
    y = 15 + Math.random() * 55;
    attempts++;
  } while (
    attempts < 50 &&
    existingPositions.some(
      (pos) => Math.abs(pos.x - x) < minDistance && Math.abs(pos.y - y) < minDistance
    )
  );

  return { x, y };
};

export const useHabits = (pondId?: string | null) => {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const [newAdultFrog, setNewAdultFrog] = useState<{ habitName: string; adultCount: number } | null>(null);
  const { user } = useAuth();

  // Get celebrated frogs from localStorage
  const getCelebratedFrogs = useCallback((): string[] => {
    try {
      const stored = localStorage.getItem(CELEBRATED_FROGS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  // Mark a frog as celebrated
  const markFrogCelebrated = useCallback((frogId: string) => {
    const celebrated = getCelebratedFrogs();
    if (!celebrated.includes(frogId)) {
      celebrated.push(frogId);
      localStorage.setItem(CELEBRATED_FROGS_KEY, JSON.stringify(celebrated));
    }
  }, [getCelebratedFrogs]);

  // Clear the new adult frog celebration
  const clearNewAdultFrog = useCallback(() => {
    if (newAdultFrog) {
      // Find the frog ID to mark as celebrated
      const adultFrog = habits.find(h => h.name === newAdultFrog.habitName && h.frog.stage === "adult");
      if (adultFrog) {
        markFrogCelebrated(adultFrog.frog.id);
      }
    }
    setNewAdultFrog(null);
  }, [newAdultFrog, habits, markFrogCelebrated]);

  const fetchHabits = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from("habits")
        .select("*")
        .eq("user_id", user.id);
      
      if (pondId) {
        query = query.eq("pond_id", pondId);
      }

      const { data: habitsData, error: habitsError } = await query;

      if (habitsError) throw habitsError;

      const { data: frogsData, error: frogsError } = await supabase
        .from("frogs")
        .select("*")
        .eq("user_id", user.id);

      if (frogsError) throw frogsError;

      const combinedHabits: Habit[] = (habitsData || []).map((habit) => {
        const frog = frogsData?.find((f) => f.habit_id === habit.id);
        return {
          id: habit.id,
          name: habit.name,
          frequencyType: habit.frequency_type,
          frequencyCount: habit.frequency_count,
          evidenceType: habit.evidence_type,
          locationLat: habit.location_lat,
          locationLng: habit.location_lng,
          locationRadius: habit.location_radius,
          scheduledTime: habit.scheduled_time,
          timezone: habit.timezone,
          frog: {
            id: frog?.id || "",
            stage: (frog?.stage || "egg") as Habit["frog"]["stage"],
            daysCompleted: frog?.days_completed || 0,
            currentStreak: frog?.current_streak || 0,
            positionX: Number(frog?.position_x) || 50,
            positionY: Number(frog?.position_y) || 50,
          },
        };
      });

      setHabits(combinedHabits);
    } catch (error: any) {
      console.error("Error fetching habits:", error);
      toast.error("Erro ao carregar hábitos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHabits();
  }, [user, pondId]);

  const addHabit = async (habitData: HabitFormData) => {
    if (!user) return;

    try {
      const existingPositions = habits.map((h) => ({
        x: h.frog.positionX,
        y: h.frog.positionY,
      }));
      const { x, y } = getRandomPosition(existingPositions);

      // Create habit
      const { data: newHabit, error: habitError } = await supabase
        .from("habits")
        .insert({
          user_id: user.id,
          pond_id: pondId,
          name: habitData.name,
          frequency_type: habitData.frequencyType,
          frequency_count: habitData.frequencyCount,
          evidence_type: habitData.evidenceType,
          is_private: habitData.isPrivate,
          location_lat: habitData.locationData?.lat ?? null,
          location_lng: habitData.locationData?.lng ?? null,
          location_radius: habitData.locationData?.radius ?? null,
          scheduled_time: habitData.scheduledTime ?? null,
          timezone: habitData.timezone ?? null,
        })
        .select()
        .single();

      if (habitError) throw habitError;

      // Create frog for the habit
      const { data: newFrog, error: frogError } = await supabase
        .from("frogs")
        .insert({
          habit_id: newHabit.id,
          user_id: user.id,
          stage: "egg",
          days_completed: 0,
          current_streak: 0,
          position_x: x,
          position_y: y,
        })
        .select()
        .single();

      if (frogError) throw frogError;

      toast.success("Hábito criado! 🥚 Um ovo apareceu na sua lagoa!");
      await fetchHabits();
    } catch (error: any) {
      console.error("Error creating habit:", error);
      toast.error("Erro ao criar hábito");
    }
  };

  const completeHabit = async (habitId: string, evidenceFile?: File) => {
    if (!user) return;

    try {
      const habit = habits.find((h) => h.id === habitId);
      if (!habit) return;

      // Block self-completion for habits requiring confirmation from another user
      if (habit.evidenceType === "confirmation") {
        toast.error("Este hábito precisa ser confirmado por outro membro da lagoa");
        return;
      }

      // Block completion without photo for photo-evidence habits
      if (habit.evidenceType === "photo" && !evidenceFile) {
        toast.error("Este hábito precisa de uma foto como evidência");
        return;
      }

      // Block completion outside geofence for location-based habits
      if (habit.evidenceType === "location" && habit.locationLat && habit.locationLng) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error("Geolocalização não suportada"));
            return;
          }
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
          });
        }).catch(() => {
          toast.error("Não foi possível obter sua localização. Permita o acesso à localização.");
          return null;
        });

        if (!position) return;

        const distance = getDistanceFromLatLng(
          position.coords.latitude,
          position.coords.longitude,
          habit.locationLat,
          habit.locationLng
        );

        const radius = habit.locationRadius || 200;
        if (distance > radius) {
          toast.error(`Você está a ${Math.round(distance)}m do local. Precisa estar dentro de ${radius}m.`);
          return;
        }
      }

      // Block completion outside time window for time-based habits
      if (habit.evidenceType === "time" && habit.scheduledTime) {
        const userTz = habit.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const now = new Date();
        // Get current time in the habit's timezone
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: userTz,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
        const parts = formatter.formatToParts(now);
        const currentHour = parseInt(parts.find(p => p.type === "hour")?.value || "0");
        const currentMinute = parseInt(parts.find(p => p.type === "minute")?.value || "0");
        const currentTotalMinutes = currentHour * 60 + currentMinute;

        const [schedH, schedM] = habit.scheduledTime.split(":").map(Number);
        const scheduledTotalMinutes = schedH * 60 + schedM;

        let diff = Math.abs(currentTotalMinutes - scheduledTotalMinutes);
        // Handle midnight wrap (e.g., 23:58 vs 00:02)
        if (diff > 720) diff = 1440 - diff;

        if (diff > 5) {
          const formattedTime = `${String(schedH).padStart(2, "0")}:${String(schedM).padStart(2, "0")}`;
          toast.error(`Você só pode marcar este hábito entre ${formattedTime} ± 5 minutos. Horário atual: ${String(currentHour).padStart(2, "0")}:${String(currentMinute).padStart(2, "0")}`);
          return;
        }
      }

      const { data: canComplete, error: checkError } = await supabase.rpc(
        "can_complete_habit",
        {
          p_habit_id: habitId,
          p_user_id: user.id,
        }
      );

      if (checkError) throw checkError;

      if (!canComplete) {
        // Get time remaining
        const { data: timeRemaining, error: timeError } = await supabase.rpc(
          "time_until_next_completion",
          {
            p_habit_id: habitId,
            p_user_id: user.id,
          }
        );

        if (timeError) {
          toast.error("Você já completou este hábito no período atual");
          return;
        }

        // Parse interval and format message
        if (timeRemaining && typeof timeRemaining === 'string') {
          const intervalMatch = timeRemaining.match(/(\d+):(\d+):(\d+)/);
          if (intervalMatch) {
            const hours = parseInt(intervalMatch[1]);
            const minutes = parseInt(intervalMatch[2]);
            
            if (hours > 24) {
              const days = Math.floor(hours / 24);
              toast.error(`Aguarde ${days} dia(s) para marcar novamente`);
            } else if (hours > 0) {
              toast.error(`Aguarde ${hours}h${minutes}m para marcar novamente`);
            } else {
              toast.error(`Aguarde ${minutes} minuto(s) para marcar novamente`);
            }
          } else {
            toast.error("Você já completou este hábito no período atual");
          }
        } else {
          toast.error("Você já completou este hábito no período atual");
        }
        return;
      }

      // Upload evidence image if provided
      let evidenceUrl: string | null = null;
      if (evidenceFile) {
        const fileExt = evidenceFile.name.split(".").pop();
        const filePath = `${user.id}/${habitId}_${Date.now()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from("habit-evidence")
          .upload(filePath, evidenceFile);
        if (uploadError) {
          toast.error("Erro ao enviar foto de evidência");
          return;
        }
        const { data: urlData } = supabase.storage
          .from("habit-evidence")
          .getPublicUrl(filePath);
        evidenceUrl = urlData.publicUrl;
      }

      // Log the completion
      await supabase.from("habit_logs").insert({
        habit_id: habitId,
        user_id: user.id,
        evidence_url: evidenceUrl,
      });

      // Update frog progress
      const newDaysCompleted = habit.frog.daysCompleted + 1;

      // Calculate streak: check if last completion was within the expected period
      let newStreak = 1; // Default: start new streak
      const { data: lastLog } = await supabase
        .from("habit_logs")
        .select("completed_at")
        .eq("habit_id", habitId)
        .eq("user_id", user.id)
        .order("completed_at", { ascending: false })
        .limit(2); // Get the 2 most recent (including the one we just inserted)

      if (lastLog && lastLog.length >= 2) {
        const previousCompletion = new Date(lastLog[1].completed_at);
        const now = new Date();
        const diffMs = now.getTime() - previousCompletion.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);

        let maxGapHours: number;
        switch (habit.frequencyType) {
          case "daily": maxGapHours = 48; break;      // Allow up to 48h gap for daily
          case "weekly": maxGapHours = 14 * 24; break; // Allow up to 2 weeks for weekly
          case "monthly": maxGapHours = 62 * 24; break; // Allow up to ~2 months for monthly
          case "yearly": maxGapHours = 730 * 24; break; // Allow up to ~2 years for yearly
          default: maxGapHours = 48;
        }

        if (diffHours <= maxGapHours) {
          newStreak = habit.frog.currentStreak + 1;
        }
      }

      let newStage = habit.frog.stage;
      if (newStage === "egg" && newDaysCompleted >= 3) {
        newStage = "tadpole";
        toast.success("🐟 Seu ovo eclodiu! Agora é um girino!");
      } else if (newStage === "tadpole" && newDaysCompleted >= 10) {
        newStage = "young";
        toast.success("🦎 Seu girino virou um sapo jovem!");
      } else if (newStage === "young" && newDaysCompleted >= 31) {
        newStage = "adult";
        // Check if this frog has been celebrated before
        const celebrated = getCelebratedFrogs();
        if (!celebrated.includes(habit.frog.id)) {
          // Count adult frogs (including this one that just became adult)
          const adultCount = habits.filter(h => h.frog.stage === "adult").length + 1;
          setNewAdultFrog({ habitName: habit.name, adultCount });
        } else {
          toast.success("🐸 Parabéns! Seu sapo está adulto! 31 dias completos!");
        }
      } else {
        toast.success("Hábito concluído! ✨");
      }

      await supabase
        .from("frogs")
        .update({
          days_completed: newDaysCompleted,
          current_streak: newStreak,
          stage: newStage,
        })
        .eq("id", habit.frog.id);

      await fetchHabits();
    } catch (error: any) {
      console.error("Error completing habit:", error);
      toast.error("Erro ao marcar hábito");
    }
  };

  const hibernateHabit = async (habitId: string) => {
    if (!user) return;

    try {
      const habit = habits.find((h) => h.id === habitId);
      if (!habit) return;

      await supabase
        .from("frogs")
        .update({ stage: "hibernating" })
        .eq("id", habit.frog.id);

      toast.success("💤 Sapo está hibernando na lagoa");
      await fetchHabits();
    } catch (error: any) {
      console.error("Error hibernating:", error);
      toast.error("Erro ao hibernar");
    }
  };

  const deleteHabit = async (habitId: string) => {
    if (!user) return;

    try {
      await supabase.from("habits").delete().eq("id", habitId);
      toast.success("Hábito removido");
      await fetchHabits();
    } catch (error: any) {
      console.error("Error deleting habit:", error);
      toast.error("Erro ao excluir hábito");
    }
  };

  const updateHabit = async (habitId: string, habitData: HabitFormData) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("habits")
        .update({
          name: habitData.name,
          frequency_type: habitData.frequencyType,
          frequency_count: habitData.frequencyCount,
          evidence_type: habitData.evidenceType,
          is_private: habitData.isPrivate,
          location_lat: habitData.locationData?.lat ?? null,
          location_lng: habitData.locationData?.lng ?? null,
          location_radius: habitData.locationData?.radius ?? null,
          scheduled_time: habitData.scheduledTime ?? null,
          timezone: habitData.timezone ?? null,
        })
        .eq("id", habitId)
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success("Hábito atualizado! ✏️");
      await fetchHabits();
    } catch (error: any) {
      console.error("Error updating habit:", error);
      toast.error("Erro ao atualizar hábito");
    }
  };

  return {
    habits,
    loading,
    addHabit,
    completeHabit,
    hibernateHabit,
    deleteHabit,
    updateHabit,
    refetch: fetchHabits,
    newAdultFrog,
    clearNewAdultFrog,
  };
};
